import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Settings } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DistributorShell, DistributorShellStatus } from '../DistributorsTab/DistributorShell';
import {
  Pax8Config,
  Pax8ConnectionTestResult,
  deletePax8Config,
  getPax8Config,
  pullPax8Prices,
  savePax8Config,
  testPax8Connection,
} from '../../services/distributorService';

const SECRET_PLACEHOLDER = '***configured***';

type TabKey = 'overview' | 'config';

interface FormState {
  clientId: string;
  clientSecret: string;
}

export function Pax8Tab() {
  const [config, setConfig] = useState<Pax8Config | null>(null);
  const [form, setForm] = useState<FormState>({ clientId: '', clientSecret: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [testResult, setTestResult] = useState<Pax8ConnectionTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await getPax8Config();
      setConfig(cfg);
      setForm({
        clientId: cfg.clientId ?? '',
        clientSecret: cfg.clientSecret ?? '',
      });
      setTab((current) => (cfg.connected ? current : 'config'));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load Pax8 configuration');
      setTab('config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const status: DistributorShellStatus = pulling
    ? { label: 'Pulling prices', tone: 'syncing', description: 'Reading Pax8 suggested retail' }
    : config?.connected
      ? { label: 'Connected', tone: 'connected', description: 'Microsoft licence standard pricing' }
      : { label: 'Not connected', tone: 'neutral', description: 'Add a Pax8 API credential' };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await savePax8Config({
        clientId: form.clientId === SECRET_PLACEHOLDER ? undefined : form.clientId,
        clientSecret: form.clientSecret === SECRET_PLACEHOLDER ? undefined : form.clientSecret,
      });
      setSuccess('Pax8 credentials saved.');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save Pax8 configuration');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const result = await testPax8Connection({
        clientId: form.clientId === SECRET_PLACEHOLDER ? undefined : form.clientId,
        clientSecret: form.clientSecret === SECRET_PLACEHOLDER ? undefined : form.clientSecret,
      });
      setTestResult(result);
      if (!result.success) setError(result.error || result.message);
    } finally {
      setTesting(false);
    }
  };

  const pull = async () => {
    setPulling(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await pullPax8Prices();
      setSuccess(result.message);
      await load();
      setTab('overview');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to pull Pax8 prices');
    } finally {
      setPulling(false);
    }
  };

  const disconnect = async () => {
    setError(null);
    setSuccess(null);
    try {
      await deletePax8Config();
      setSuccess('Pax8 disconnected. Prices already saved on the Pax8 list are unchanged.');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect Pax8');
    }
  };

  return (
    <DistributorShell
      lockup={<Pax8Lockup />}
      title="Pax8 Connector"
      currentCrumb="Pax8"
      status={status}
      actions={
        <>
          <Button variant="outline" className="gap-2" onClick={() => setTab('config')} disabled={loading}>
            <Settings className="h-4 w-4" />
            Configure
          </Button>
          <Button className="gap-2" onClick={() => void pull()} disabled={!config?.connected || pulling || loading}>
            {pulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Pull standard prices
          </Button>
        </>
      }
      tabs={[
        { key: 'overview', label: 'Overview' },
        { key: 'config', label: 'Configuration' },
      ]}
      activeTab={tab}
      onTabChange={setTab}
      banners={
        <>
          {error && <Banner tone="error">{error}</Banner>}
          {success && <Banner tone="ok">{success}</Banner>}
        </>
      }
    >
      {loading ? (
        <div className="p-8 text-sm text-slate-500">Loading Pax8…</div>
      ) : tab === 'overview' ? (
        <Overview config={config} testResult={testResult} />
      ) : (
        <Configuration
          form={form}
          setForm={setForm}
          saving={saving}
          testing={testing}
          testResult={testResult}
          connected={config?.connected === true}
          onSave={() => void save()}
          onTest={() => void test()}
          onDisconnect={() => void disconnect()}
        />
      )}
    </DistributorShell>
  );
}

function Overview({ config, testResult }: { config: Pax8Config | null; testResult: Pax8ConnectionTestResult | null }) {
  const pull = config?.lastPull;
  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-semibold text-slate-900">Microsoft licence standard pricing</h2>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Pull reads every Microsoft product in your Pax8 catalogue and saves prices only when Pax8
          reports AUD. That currency comes from your invoices, or from a subscription when you have
          no invoice yet. A US dollar amount is not converted and is not saved. The dollar amount is
          Pax8 suggested retail for the lowest quantity band. Partner buy rate is your cost and is not
          written onto the customer price list.
        </p>
        <ul className="mt-3 space-y-1 text-sm text-slate-600">
          <li>Monthly billing, commitment of a month or less → month to month</li>
          <li>Monthly billing, 12-month commitment → 12 months, billed monthly</li>
          <li>Annual billing, 12-month commitment → 12 months, billed annually</li>
        </ul>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          A product is attached to a licence when its vendor SKU or legacy vendor SKU is the Microsoft
          Graph licence id, or when the vendor SKU is a Partner Center offer id that maps to that licence.
          Pax8&apos;s own product code is not used. The full product description is read from the product
          record and shown to customers. A truncated &quot;...&quot; is not stored.
        </p>
        <p className="mt-3 text-sm text-slate-500">
          Telstra, Dicker Data and any price you typed for a term Pax8 did not return are left as they are.
          Customers see these prices only after Pax8 is their distributor, or the default when you have not chosen one.
        </p>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <Stat label="Licences priced" value={pull ? String(pull.licences) : '—'} />
        <Stat label="Terms saved" value={pull ? String(pull.terms) : '—'} />
        <Stat label="Last pull" value={pull?.lastPullAt ? new Date(pull.lastPullAt).toLocaleString() : 'Not yet'} />
      </section>

      {testResult && (
        <p className={`text-sm ${testResult.success ? 'text-emerald-700' : 'text-rose-700'}`}>{testResult.message}</p>
      )}
    </div>
  );
}

function Configuration({
  form,
  setForm,
  saving,
  testing,
  testResult,
  connected,
  onSave,
  onTest,
  onDisconnect,
}: {
  form: FormState;
  setForm: (next: FormState) => void;
  saving: boolean;
  testing: boolean;
  testResult: Pax8ConnectionTestResult | null;
  connected: boolean;
  onSave: () => void;
  onTest: () => void;
  onDisconnect: () => void;
}) {
  return (
    <div className="p-6 max-w-xl space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">API credential</h2>
          <ol className="mt-2 list-decimal pl-5 space-y-1 text-sm text-slate-600">
            <li>In Pax8, open Integrations → API credentials.</li>
            <li>Create a credential named Aitryx. Copy the client id and client secret. The secret is shown once.</li>
            <li>Paste them here and test the connection. The token audience is https://api.pax8.com.</li>
            <li>Pull standard prices. Review the Pax8 column under Marketplace → Microsoft licences before customers see it.</li>
          </ol>
        </div>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">Client id</span>
          <input
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={form.clientId}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, clientId: event.target.value })}
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">Client secret</span>
          <input
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            type="password"
            value={form.clientSecret}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, clientSecret: event.target.value })}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <Button className="gap-2" onClick={onSave} disabled={saving || testing}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
          <Button variant="outline" className="gap-2" onClick={onTest} disabled={saving || testing}>
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Test connection
          </Button>
          {connected && (
            <Button variant="outline" onClick={onDisconnect}>
              Disconnect
            </Button>
          )}
        </div>
        {testResult && (
          <p className={`text-sm ${testResult.success ? 'text-emerald-700' : 'text-rose-700'}`}>{testResult.message}</p>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'ok'; children: React.ReactNode }) {
  return (
    <div className={`mx-6 mt-4 rounded-md border px-3 py-2 text-sm ${tone === 'error' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
      {children}
    </div>
  );
}

function Pax8Lockup() {
  return (
    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2">
      <div className="w-7 h-7 rounded-md grid place-items-center bg-slate-900 text-white text-[11px] font-bold">P8</div>
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium">Pax8</div>
        <div className="text-[13px] font-semibold text-slate-900 -mt-0.5">Microsoft licensing</div>
      </div>
    </div>
  );
}
